"use client"

import { useTheme } from "next-themes"
import { type RefObject, useCallback, useEffect, useState } from "react"
import { flushSync } from "react-dom"
import { trackThemeToggle } from "@/lib/analytics"
import { playToggleSound, primeToggleSound } from "@/lib/theme-toggle-sound"

export interface UseAnimatedThemeToggleOptions {
    duration?: number
    sound?: boolean
}

export function useAnimatedThemeToggle(
    originRef: RefObject<HTMLElement | null>,
    options: UseAnimatedThemeToggleOptions = {}
) {
    const { duration = 550, sound = true } = options
    const { theme, setTheme } = useTheme()
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (sound) primeToggleSound()
    }, [sound])

    const isDark = theme === "dark"

    const toggleTheme = useCallback(async () => {
        const el = originRef.current

        const viewportWidth = document.documentElement.clientWidth
        const viewportHeight = document.documentElement.clientHeight

        // An origin element anchors the reveal on itself; without one (the
        // keyboard shortcut) it opens from the middle of the screen.
        let x = viewportWidth / 2
        let y = viewportHeight / 2

        if (el) {
            const { top, left, width, height } = el.getBoundingClientRect()
            x = left + width / 2
            y = top + height / 2
        }

        // Far enough to swallow the most distant corner, so no sliver of the
        // old theme survives the wipe.
        const cornerDistance = Math.hypot(
            Math.max(x, viewportWidth - x),
            Math.max(y, viewportHeight - y)
        )

        // The clip-path resolves against ::view-transition-new(root)'s own box,
        // and browsers disagree on how big that is — px coordinates only land
        // correctly where it happens to match the viewport. Percentages track
        // the box itself, so 50%/50% is the middle of the screen everywhere.
        // A percentage radius resolves against hypot(w, h) / sqrt(2), hence the
        // conversion below rather than a plain ratio.
        const originX = (x / viewportWidth) * 100
        const originY = (y / viewportHeight) * 100
        const maxRadius =
            (cornerDistance * Math.SQRT2 * 100) /
            Math.hypot(viewportWidth, viewportHeight)

        if (sound) playToggleSound()

        if (document.startViewTransition) {
            await document.startViewTransition(() => {
                flushSync(() => {
                    setTheme(isDark ? "light" : "dark")
                })
            }).ready

            document.documentElement.animate(
                {
                    clipPath: [
                        `circle(0% at ${originX}% ${originY}%)`,
                        `circle(${maxRadius}% at ${originX}% ${originY}%)`,
                    ],
                },
                {
                    duration,
                    easing: "cubic-bezier(0.33, 1, 0.68, 1)",
                    pseudoElement: "::view-transition-new(root)",
                }
            )
        } else {
            setTheme(isDark ? "light" : "dark")
        }

        const newTheme = isDark ? "light" : "dark"
        trackThemeToggle(newTheme)
    }, [duration, isDark, originRef, setTheme, sound])

    return { isDark, mounted, toggleTheme }
}
